import type { SnakeGameDetails } from "@/entities/Activity/types";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader } from "rsuite";
import { GameContext } from "../../../../app/context/GameContext";
import { useGetActivitiesQuery } from "../../../../entities/Activity/activityApi";
import { BlurredImageGame } from "../../../../features/activities/BlurredImageGame";
import { MemoryCardGame } from "../../../../features/activities/MemoryCardsGame/MemoryCardsGame";
import { Snake } from "../../../../features/activities/Snake/Snake";
import { GameModal } from "../GameModal/GameModal";
import style from "./GamesList.module.scss";

export const GamesList = () => {
	const { activityType = "" } = useParams();
	const { data = [], isFetching, error } = useGetActivitiesQuery(activityType);
	const [openGameModal, setOpenGameModal] = useState(false);
	const [localType, setLocalType] = useState("");
	const [gameId, setGameId] = useState("");
	const [gameData, setGameData] = useState<SnakeGameDetails | null>(null);

	const { setGameModalOpen } = useContext(GameContext);

	useEffect(() => {
		const foundGame = data.find((item) => item._id === gameId)
			?.details as SnakeGameDetails;

		if (foundGame) {
			setGameData(foundGame);
		} else {
			setGameData(null);
		}
	}, [gameId, data]);

	if (isFetching) {
		return <Loader center content="loading" vertical />;
	}

	if (error) {
		return <div>Please try again</div>;
	}

	const switchSourceTarget = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (gameData) {
			const swappedWords = gameData.words.map((word) => ({
				sourceLang: word.targetLang,
				targetLang: word.sourceLang,
			}));
			setGameData({ ...gameData, words: swappedWords });
		}
	};

	const blurredData = (
		data.find((item) => item._id === gameId)?.details as {
			data: {
				word: string;
				image: string;
			}[];
		}
	)?.data as { word: string; image: string }[];

	const memoryCardGame = (
		data.find((item) => item._id === gameId)?.details as unknown as {
			data: {
				value: string;
				pic: string;
			}[];
		}
	)?.data as { value: string; pic: string }[];

	return (
		<div className={style.gamesList}>
			{openGameModal && gameData && (
				<GameModal setOpenGameModal={setOpenGameModal}>
					{localType === "snake-game" && <Snake data={gameData.words} />}
					{localType === "blurred-image-game" && (
						<BlurredImageGame data={blurredData} />
					)}
					{localType === "memory-card-game" && (
						<MemoryCardGame data={memoryCardGame} />
					)}
				</GameModal>
			)}
			{data.map((item) => {
				return (
					<div
						onClick={() => {
							setOpenGameModal(true);
							setGameModalOpen(true);
							setLocalType(item.type);
							setGameId(item._id as string);
						}}
						onKeyUp={(event) => {
							if (event.key === "Enter" || event.key === " ") {
								event.preventDefault();
								setOpenGameModal(true);
								setLocalType(item.type);
								setGameId(item._id as string);
							}
						}}
						className={style.gamesList__item}
						key={item._id}
					>
						{item.type === "snake-game" && (
							<button type="button" onClick={switchSourceTarget}>
								Switch
							</button>
						)}
						<p className={style.gamesList__item__title}>{item.title}</p>
						<p className={style.gamesList__item__description}>
							{(item.details as { description: string }).description}
						</p>
						<ul className={style.gamesList__item__words}>
							{gameData?.words?.map((word) => (
								<li key={word.sourceLang}>
									<strong>{word?.sourceLang}</strong> - {word?.targetLang}
								</li>
							))}
						</ul>
					</div>
				);
			})}
		</div>
	);
};
